import React, { useState } from "react";
import { Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { apiHandler } from '../../api/apihandler';
import { BILLING_PAD_SETUP } from '../../api/constants';
import LoaderModal from '../loaderModal';
import { useStripe } from '@stripe/react-stripe-js';

// Phase 52 - Stripe ACSS Debit/Canadian PAD setup. Mirrors
// StripeCardForBilling's structure (same apiHandler/LoaderModal/Swal
// pattern), with one deliberate difference: this component NEVER collects
// a bank account/transit/institution number itself. `acss_debit` is
// omitted from the payment_method data passed to
// `stripe.confirmAcssDebitSetup` below, which per Stripe's own
// documentation means "Stripe will help to collect bank account details
// and verification" - Stripe.js opens its own hosted modal for that,
// and the bank details never pass through this application's code or
// servers at all. Only the non-bank billing_details (name/email/address)
// collected here are sent.
function PadSetupForBilling({ defaultName, defaultEmail, onSuccess, onCancel }) {
    const stripe = useStripe();
    const [showLoader, setShowLoader] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [name, setName] = useState(defaultName || '');
    const [email, setEmail] = useState(defaultEmail || '');
    const [addressLine1, setAddressLine1] = useState('');
    const [city, setCity] = useState('');
    const [postalCode, setPostalCode] = useState('');

    const canSubmit = name.trim() && email.trim() && addressLine1.trim() && city.trim() && postalCode.trim();

    const submitSetup = async () => {
        if (submitting || !canSubmit) {
            return;
        }
        setSubmitting(true);
        try {
            setShowLoader(true);
            const setupRes = await apiHandler(BILLING_PAD_SETUP, "POST", {});
            if (setupRes?.isAxiosError || (setupRes?.response && setupRes?.data === undefined)) {
                Swal.fire({
                    title: 'Error',
                    text: setupRes?.response?.data?.data?.message || setupRes?.response?.data?.message || 'Unable to start PAD setup. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'Okay'
                });
                return;
            }

            const clientSecret = setupRes?.data?.data?.client_secret;
            if (!clientSecret) {
                Swal.fire({ title: 'Error', text: setupRes?.data?.data?.message || 'Unable to start PAD setup. Please try again.', icon: 'error', confirmButtonText: 'Okay' });
                return;
            }

            // Stripe's own hosted modal collects the bank account number,
            // transit number, and institution number from here, plus
            // presents the PAD mandate agreement for the parent to accept -
            // none of that ever reaches this component or this app's
            // backend. `acss_debit` is intentionally omitted below.
            const result = await stripe.confirmAcssDebitSetup(clientSecret, {
                payment_method: {
                    billing_details: {
                        name,
                        email,
                        address: { line1: addressLine1, city, postal_code: postalCode, country: 'CA' },
                    },
                },
            });

            if (result.error) {
                Swal.fire({
                    title: 'PAD Setup Failed',
                    text: result.error.message || 'Unable to complete PAD setup. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'Okay'
                });
                return;
            }

            // The SetupIntent resolving here means the parent completed
            // Stripe's hosted collection/verification/mandate-acceptance
            // flow - the mandate only becomes ACTIVE in HarmonyDayCare's
            // own records once the `setup_intent.succeeded` webhook is
            // processed (asynchronous, server-side), so this is phrased as
            // "submitted", not "active", and the caller is expected to
            // poll/refresh PAD status rather than assume it is usable yet.
            Swal.fire({
                title: 'Bank Payment Setup Submitted',
                text: 'Your bank payment authorization has been submitted. It may take a moment to become active.',
                icon: 'success',
                confirmButtonText: 'Okay'
            });
            onSuccess && onSuccess();
        } catch (error) {
            Swal.fire({
                title: 'PAD Setup Failed',
                text: 'Something went wrong while setting up bank payment. Please try again.',
                icon: 'error',
                confirmButtonText: 'Okay'
            });
        } finally {
            setShowLoader(false);
            setSubmitting(false);
        }
    };

    return (
        <>
            <LoaderModal showLoader={showLoader} loaderData="Opening secure bank setup..." />
            <p className="text-muted small">
                Your bank account number is never entered here - clicking "Continue" opens Stripe's secure,
                bank-verified setup window.
            </p>
            <Form.Group className="mb-2">
                <Form.Label>Full Name</Form.Label>
                <Form.Control type="text" value={name} onChange={(e) => setName(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-2">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-2">
                <Form.Label>Address</Form.Label>
                <Form.Control type="text" placeholder="Street address" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} />
            </Form.Group>
            <div className="d-flex gap-2 mb-2">
                <Form.Control type="text" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
                <Form.Control type="text" placeholder="Postal Code" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3">
                <button type="button" className="btn btn-outline-secondary" disabled={submitting} onClick={onCancel}>
                    Cancel
                </button>
                <button type="button" className="btn btn-primary" disabled={submitting || !stripe || !canSubmit} onClick={submitSetup}>
                    {submitting ? 'Processing...' : 'Continue to Secure Bank Setup'}
                </button>
            </div>
        </>
    );
}
export default PadSetupForBilling;
